import { AnimationGroup, Observer } from "babylonjs";
import { IAssetVideo, IAssetModel, IAssetAudio, IAssetTexture } from "@webcore/core/loader/loader";
import { ContentInstance, LoopMode } from "@core/content/types";
import appEvents from "@core/events/events";
import { getCurrentCue } from "@core/utils/subtitles/getCurrentCue";

export interface ContentPlaybackControllerOptions {
  /** Whether content should loop (default: true) */
  loop?: boolean;
  /** 
   * How media should loop:
   * - "synchronized": One media type drives the loop, all restart together (default)
   * - "independent": Each media type loops on its own timeline
   */
  loopMode?: LoopMode;
  onLoopComplete?: () => void;
}

export type PlaybackState = 'stopped' | 'playing' | 'paused';

/**
 * Controls content playback and visibility.
 */
export class ContentPlaybackController {
  // Internal state - trust this, not Babylon.js state
  private playbackState: PlaybackState = 'stopped';
  
  private lastPlayEmittedForId?: string;
  private animationLoopObservers: Map<AnimationGroup, Observer<AnimationGroup>> = new Map();
  private videoLoopElement?: HTMLVideoElement;
  private videoLoopEndedHandler?: () => void;
  private audioLoopElement?: HTMLAudioElement;
  private audioLoopEndedHandler?: () => void;
  private subtitleTimeUpdateHandlers: Map<HTMLMediaElement, () => void> = new Map();
  private loop: boolean;
  private loopMode: LoopMode;
  private onLoopComplete?: () => void;

  constructor(options?: ContentPlaybackControllerOptions) {
    this.loop = options?.loop ?? true; // Default to looping
    this.loopMode = options?.loopMode ?? "independent"; // Default to independent
    this.onLoopComplete = options?.onLoopComplete;
  }

  // ─────────────────────────────────────────────────────────────
  // VISIBILITY - separate from playback
  // ─────────────────────────────────────────────────────────────

  /**
   * Show content (make visible). Does NOT affect playback.
   */
  public show(instance: ContentInstance): void {
    if (!instance?.isLoaded || instance.assets.length === 0) return;

    instance.assets.forEach(asset => {
      if (asset.type === "video") {
        const videoPlane = instance.videoPlane;
        if (videoPlane) {
          videoPlane.setEnabled(true);
          videoPlane.isVisible = true;
        }
      } else if (asset.type === "model") {
        const modelAsset = asset as IAssetModel;
        if (modelAsset.meshRoot) {
          modelAsset.meshRoot.setEnabled(true);
        }
        if (modelAsset.meshes) {
          modelAsset.meshes.forEach(mesh => {
            mesh.setEnabled(true);
            mesh.isVisible = true;
          });
        }
      } else if (asset.type === "texture") {
        const textureAsset = asset as IAssetTexture & { plane?: { setEnabled: (v: boolean) => void; isVisible: boolean } };
        if (textureAsset.plane) {
          textureAsset.plane.setEnabled(true);
          textureAsset.plane.isVisible = true;
        }
      }
    });

    instance.isVisible = true;
  }

  /**
   * Hide content (make invisible). Does NOT affect playback.
   */
  public hide(instance: ContentInstance): void {
    if (!instance || instance.assets.length === 0) return;

    instance.assets.forEach(asset => {
      if (asset.type === "video") {
        const videoPlane = instance.videoPlane;
        if (videoPlane) {
          videoPlane.setEnabled(false);
          videoPlane.isVisible = false;
        }
      } else if (asset.type === "model") {
        const modelAsset = asset as IAssetModel;
        if (modelAsset.meshRoot) {
          modelAsset.meshRoot.setEnabled(false);
        }
        if (modelAsset.meshes) {
          modelAsset.meshes.forEach(mesh => {
            mesh.setEnabled(false);
            mesh.isVisible = false;
          });
        }
      } else if (asset.type === "texture") {
        const textureAsset = asset as IAssetTexture & { plane?: { setEnabled: (v: boolean) => void; isVisible: boolean } };
        if (textureAsset.plane) {
          textureAsset.plane.setEnabled(false);
          textureAsset.plane.isVisible = false;
        }
      }
    });

    instance.isVisible = false;
  }

  // ─────────────────────────────────────────────────────────────
  // PLAYBACK CONTROL
  // ─────────────────────────────────────────────────────────────

  /**
   * Get the effective loop mode for a content instance
   */
  private getEffectiveLoopMode(instance: ContentInstance): LoopMode {
    return instance.contentItem?.loopMode ?? this.loopMode;
  }

  /**
   * Play content from the beginning. Shows content and starts all media.
   * Throws if any media fails to play.
   */
  public async play(instance: ContentInstance): Promise<void> {
    if (!instance?.isLoaded || instance.assets.length === 0) return;

    this.show(instance);

    const playPromises: Promise<void>[] = [];
    const effectiveLoopMode = this.getEffectiveLoopMode(instance);

    for (const asset of instance.assets) {
      if (asset.type === "video") {
        const videoAsset = asset as IAssetVideo;
        if (videoAsset.videoElement) {
          playPromises.push(this.startVideo(videoAsset, effectiveLoopMode));
        }
      } else if (asset.type === "model") {
        this.startAnimations(asset as IAssetModel);
      } else if (asset.type === "audio") {
        const audioAsset = asset as IAssetAudio;
        if (audioAsset.audioElement) {
          playPromises.push(this.startAudio(audioAsset, effectiveLoopMode));
        } else if (audioAsset.audio) {
          audioAsset.audio.play();
        }
      }
    }

    try {
      await Promise.all(playPromises);
      this.playbackState = 'playing';
      this.startSubtitleTracking(instance);
    } catch (err) {
      // Failed to play - reset everything
      this.stopAllMedia(instance);
      this.hide(instance);
      this.playbackState = 'stopped';
      throw err;
    }
  }

  /**
   * Pause playback. Content remains visible, media pauses at current position.
   */
  public pause(instance: ContentInstance): void {
    if (!instance || instance.assets.length === 0) return;
    if (this.playbackState !== 'playing') return; // Can only pause if playing

    instance.assets.forEach(asset => {
      if (asset.type === "video") {
        const videoAsset = asset as IAssetVideo;
        videoAsset.videoElement?.pause();
      } else if (asset.type === "model") {
        this.pauseAnimations(asset as IAssetModel);
      } else if (asset.type === "audio") {
        const audioAsset = asset as IAssetAudio;
        if (audioAsset.audio) {
          audioAsset.audio.pause();
        } else if (audioAsset.audioElement) {
          audioAsset.audioElement.pause();
        }
      }
    });

    this.playbackState = 'paused';
  }

  /**
   * Resume playback from paused state. Does NOT change visibility.
   * Throws if any media fails to resume.
   */
  public async resume(instance: ContentInstance): Promise<void> {
    if (!instance || instance.assets.length === 0) return;
    if (this.playbackState !== 'paused') {
      // If not paused, just play from beginning
      if (this.playbackState === 'stopped') {
        await this.play(instance);
      }
      return;
    }

    const playPromises: Promise<void>[] = [];

    for (const asset of instance.assets) {
      if (asset.type === "video") {
        const videoAsset = asset as IAssetVideo;
        if (videoAsset.videoElement) {
          playPromises.push(videoAsset.videoElement.play());
        }
      } else if (asset.type === "model") {
        this.resumeAnimations(asset as IAssetModel);
      } else if (asset.type === "audio") {
        const audioAsset = asset as IAssetAudio;
        if (audioAsset.audio) {
          audioAsset.audio.play();
        } else if (audioAsset.audioElement) {
          audioAsset.audioElement.muted = false;
          playPromises.push(audioAsset.audioElement.play());
        }
      }
    }

    try {
      await Promise.all(playPromises);
      this.playbackState = 'playing';
      this.startSubtitleTracking(instance);
    } catch (err) {
      // Failed to resume - pause everything again
      this.pauseAllMedia(instance);
      this.playbackState = 'paused';
      throw err;
    }
  }

  /**
   * Stop playback completely. Hides content and resets to beginning.
   */
  public stop(instance: ContentInstance, hide: boolean = true): void {
    if (!instance || instance.assets.length === 0) return;

    this.cleanupLoopDetection(instance);
    this.stopAllMedia(instance);
    if (hide) {
      this.hide(instance);
    }
    this.playbackState = 'stopped';
    this.lastPlayEmittedForId = undefined;
  }

  /**
   * Restart content from beginning (stop + play).
   * Throws if any media fails to play.
   */
  public async restart(instance: ContentInstance): Promise<void> {
    if (!instance || instance.assets.length === 0) return;
    
    this.cleanupLoopDetection(instance);
    this.stopAllMedia(instance);
    this.playbackState = 'stopped';
    
    // Wait a frame then play
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
    await this.play(instance);
  }

  // ─────────────────────────────────────────────────────────────
  // MEDIA HELPERS - Private, consistent implementation
  // ─────────────────────────────────────────────────────────────

  private async startVideo(videoAsset: IAssetVideo, effectiveLoopMode: LoopMode): Promise<void> {
    const video = videoAsset.videoElement;
    if (!video) throw new Error("No video element");

    // Set source if needed
    if (!video.src || !video.src.endsWith(videoAsset.path)) {
      video.src = videoAsset.path;
      video.load();
    }

    video.currentTime = 0;
    // In independent mode, let video loop natively; in synchronized mode, we handle looping via ended event
    video.loop = effectiveLoopMode === "independent" && this.loop;
    await video.play(); // Throws on failure
  }

  private async startAudio(audioAsset: IAssetAudio, effectiveLoopMode: LoopMode): Promise<void> {
    const audio = audioAsset.audioElement;
    if (!audio) throw new Error("No audio element");

    // Set source if needed
    if (!audio.src || !audio.src.endsWith(audioAsset.path)) {
      audio.src = audioAsset.path;
      audio.load();
      await this.waitForAudioReady(audio);
    }

    audio.currentTime = 0;
    audio.muted = false;
    // In independent mode, let audio loop natively; in synchronized mode, we handle looping via ended event
    audio.loop = effectiveLoopMode === "independent" && this.loop;
    await audio.play(); // Throws on failure
  }

  /**
   * Stop all media without changing state (used for error recovery)
   */
  private stopAllMedia(instance: ContentInstance): void {
    instance.assets.forEach(asset => {
      if (asset.type === "video") {
        const video = (asset as IAssetVideo).videoElement;
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
      } else if (asset.type === "model") {
        this.stopAnimations(asset as IAssetModel);
      } else if (asset.type === "audio") {
        const audioAsset = asset as IAssetAudio;
        if (audioAsset.audio) {
          audioAsset.audio.stop();
        } else if (audioAsset.audioElement) {
          audioAsset.audioElement.pause();
          audioAsset.audioElement.currentTime = 0;
        }
      }
    });
  }

  /**
   * Pause all media without changing state (used for error recovery)
   */
  private pauseAllMedia(instance: ContentInstance): void {
    instance.assets.forEach(asset => {
      if (asset.type === "video") {
        (asset as IAssetVideo).videoElement?.pause();
      } else if (asset.type === "model") {
        this.pauseAnimations(asset as IAssetModel);
      } else if (asset.type === "audio") {
        const audioAsset = asset as IAssetAudio;
        if (audioAsset.audio) {
          audioAsset.audio.pause();
        } else if (audioAsset.audioElement) {
          audioAsset.audioElement.pause();
        }
      }
    });
  }

  private waitForAudioReady(audio: HTMLAudioElement): Promise<void> {
    return new Promise((resolve, reject) => {
      if (audio.readyState >= 3) {
        resolve();
        return;
      }
      
      const onReady = () => {
        audio.removeEventListener('canplaythrough', onReady);
        audio.removeEventListener('error', onError);
        resolve();
      };
      const onError = (e: Event) => {
        audio.removeEventListener('canplaythrough', onReady);
        audio.removeEventListener('error', onError);
        reject(new Error(`Audio load error: ${(e as ErrorEvent).message || 'unknown'}`));
      };
      
      audio.addEventListener('canplaythrough', onReady, { once: true });
      audio.addEventListener('error', onError, { once: true });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // ANIMATION HELPERS - Consistent stop/start pattern
  // ─────────────────────────────────────────────────────────────

  /**
   * Start animations from beginning
   */
  private startAnimations(model: IAssetModel): void {
    const anims = model?.animations;
    if (!anims || anims.length === 0) return;

    anims.forEach((animGroup: any) => {
      // Always stop first to ensure clean state
      if (typeof animGroup.stop === "function") {
        animGroup.stop();
      }
      // Then start from beginning
      if (typeof animGroup.start === "function") {
        animGroup.start(this.loop, 1.0, animGroup.from, animGroup.to);
      }
    });
  }

  /**
   * Pause animations at current frame
   */
  private pauseAnimations(model: IAssetModel): void {
    const anims = model?.animations;
    if (!anims || anims.length === 0) return;

    anims.forEach((animGroup: any) => {
      if (typeof animGroup.pause === "function") {
        animGroup.pause();
      }
    });
  }

  /**
   * Resume animations from paused state
   */
  private resumeAnimations(model: IAssetModel): void {
    const anims = model?.animations;
    if (!anims || anims.length === 0) return;

    anims.forEach((animGroup: any) => {
      // Skip if already playing
      if (animGroup.isPlaying) return;
      
      // Try play() first (resumes from paused position in Babylon.js)
      if (typeof animGroup.play === "function") {
        animGroup.play(this.loop);
        if (animGroup.isPlaying) return; // Success
      }
      
      // Fallback: force restart if play() didn't work
      if (typeof animGroup.stop === "function") {
        animGroup.stop();
      }
      if (typeof animGroup.start === "function") {
        animGroup.start(this.loop, 1.0, animGroup.from || 0, animGroup.to);
      }
    });
  }

  /**
   * Stop animations and reset to frame 0
   */
  private stopAnimations(model: IAssetModel): void {
    const anims = model?.animations;
    if (!anims || anims.length === 0) return;

    anims.forEach((animGroup: any) => {
      if (typeof animGroup.stop === "function") {
        animGroup.stop();
      }
      if (typeof animGroup.goToFrame === "function") {
        animGroup.goToFrame(0);
      }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // SUBTITLE TRACKING
  // ─────────────────────────────────────────────────────────────

  private startSubtitleTracking(instance: ContentInstance): void {
    this.stopSubtitleTracking();
    
    if (!instance.subtitleCues || instance.subtitleCues.length === 0) return;
    
    for (const asset of instance.assets) {
      let mediaElement: HTMLMediaElement | null = null;
      
      if (asset.type === "video") {
        mediaElement = (asset as IAssetVideo).videoElement || null;
      } else if (asset.type === "audio") {
        mediaElement = (asset as IAssetAudio).audioElement || null;
      }
      
      if (mediaElement) {
        const handler = () => this.updateSubtitles(instance);
        mediaElement.addEventListener('timeupdate', handler);
        this.subtitleTimeUpdateHandlers.set(mediaElement, handler);
        this.updateSubtitles(instance);
        break;
      }
    }
  }

  private stopSubtitleTracking(): void {
    this.subtitleTimeUpdateHandlers.forEach((handler, element) => {
      element.removeEventListener('timeupdate', handler);
    });
    this.subtitleTimeUpdateHandlers.clear();
    appEvents.emit('subtitle-cue', null);
  }

  private updateSubtitles(instance: ContentInstance): void {
    if (!instance.subtitleCues || instance.subtitleCues.length === 0) return;

    let currentTime = 0;
    for (const asset of instance.assets) {
      if (asset.type === "video" && (asset as IAssetVideo).videoElement) {
        currentTime = (asset as IAssetVideo).videoElement!.currentTime;
        break;
      } else if (asset.type === "audio" && (asset as IAssetAudio).audioElement) {
        currentTime = (asset as IAssetAudio).audioElement!.currentTime;
        break;
      }
    }

    const cue = getCurrentCue(instance.subtitleCues, currentTime);
    appEvents.emit('subtitle-cue', cue);
  }

  // ─────────────────────────────────────────────────────────────
  // LOOP DETECTION
  // ─────────────────────────────────────────────────────────────

  public setupContentLoopDetection(instance: ContentInstance): void {
    this.clearAnimationLoopObservers();

    // In independent mode, each media loops natively - no need to set up synchronized detection
    // Video/audio use native loop attribute, animations loop via AnimationGroup.start(loop=true)
    const effectiveLoopMode = this.getEffectiveLoopMode(instance);
    if (effectiveLoopMode === "independent") {
      return;
    }

    // Auto-detect: find what media types are available
    let videoElement: HTMLVideoElement | undefined;
    let audioElement: HTMLAudioElement | undefined;
    let modelWithAnimations: { animGroups: AnimationGroup[] } | undefined;

    for (const asset of instance.assets) {
      if (asset.type === "video") {
        const ve = (asset as IAssetVideo).videoElement;
        if (ve) videoElement = ve;
      }
      if (asset.type === "audio") {
        const ae = (asset as IAssetAudio).audioElement;
        if (ae) audioElement = ae;
      }
      if (asset.type === "model") {
        const modelAsset = asset as IAssetModel;
        const anims = modelAsset?.animations;
        if (anims && anims.length > 0) {
          const animGroups = anims.filter((ag: any) => ag instanceof AnimationGroup) as AnimationGroup[];
          if (animGroups.length > 0) {
            modelWithAnimations = { animGroups };
          }
        }
      }
    }

    // Auto-detect which media drives the loop: prefer video > audio > animation
    if (videoElement) {
      this.setupVideoLoopDetection(videoElement);
    } else if (audioElement) {
      this.setupAudioLoopDetection(audioElement);
    } else if (modelWithAnimations) {
      // Use longest animation as the loop driver
      const { animGroups } = modelWithAnimations;
      const targetGroup = animGroups.reduce((longest, current) => {
        return (current.to - current.from) > (longest.to - longest.from) ? current : longest;
      });
      this.setupAnimationLoopDetection(targetGroup);
    }
    // If no loopable media found, do nothing
  }

  private setupVideoLoopDetection(video: HTMLVideoElement): void {
    if (this.videoLoopEndedHandler && this.videoLoopElement) {
      this.videoLoopElement.removeEventListener('ended', this.videoLoopEndedHandler);
    }
    
    const handler = () => {
      appEvents.emit('animation-loop-complete');
      this.onLoopComplete?.();
    };
    video.addEventListener('ended', handler);
    this.videoLoopElement = video;
    this.videoLoopEndedHandler = handler;
  }

  private setupAudioLoopDetection(audio: HTMLAudioElement): void {
    if (this.audioLoopEndedHandler && this.audioLoopElement) {
      this.audioLoopElement.removeEventListener('ended', this.audioLoopEndedHandler);
    }
    
    const handler = () => {
      appEvents.emit('animation-loop-complete');
      this.onLoopComplete?.();
    };
    audio.addEventListener('ended', handler);
    this.audioLoopElement = audio;
    this.audioLoopEndedHandler = handler;
  }

  private setupAnimationLoopDetection(animGroup: AnimationGroup): void {
    const existingObserver = this.animationLoopObservers.get(animGroup);
    if (existingObserver && animGroup.onAnimationGroupEndObservable) {
      animGroup.onAnimationGroupEndObservable.remove(existingObserver);
      this.animationLoopObservers.delete(animGroup);
    }

    if (animGroup.onAnimationGroupEndObservable) {
      const observer = animGroup.onAnimationGroupEndObservable.add(() => {
        appEvents.emit('animation-loop-complete');
        this.onLoopComplete?.();
      });
      this.animationLoopObservers.set(animGroup, observer);
    }
  }

  public cleanupLoopDetection(instance?: ContentInstance): void {
    if (this.videoLoopEndedHandler && this.videoLoopElement) {
      this.videoLoopElement.removeEventListener('ended', this.videoLoopEndedHandler);
      this.videoLoopElement = undefined;
      this.videoLoopEndedHandler = undefined;
    } else if (this.videoLoopEndedHandler && instance?.assets) {
      instance.assets.forEach(asset => {
        if (asset.type === "video") {
          (asset as IAssetVideo).videoElement?.removeEventListener('ended', this.videoLoopEndedHandler!);
        }
      });
      this.videoLoopEndedHandler = undefined;
    }

    if (this.audioLoopEndedHandler && this.audioLoopElement) {
      this.audioLoopElement.removeEventListener('ended', this.audioLoopEndedHandler);
      this.audioLoopElement = undefined;
      this.audioLoopEndedHandler = undefined;
    } else if (this.audioLoopEndedHandler && instance?.assets) {
      instance.assets.forEach(asset => {
        if (asset.type === "audio") {
          (asset as IAssetAudio).audioElement?.removeEventListener('ended', this.audioLoopEndedHandler!);
        }
      });
      this.audioLoopEndedHandler = undefined;
    }

    this.clearAnimationLoopObservers();
    
    this.stopSubtitleTracking();
  }

  private clearAnimationLoopObservers(): void {
    this.animationLoopObservers.forEach((observer, animGroup) => {
      animGroup.onAnimationGroupEndObservable?.remove(observer);
    });
    this.animationLoopObservers.clear();
  }

  // ─────────────────────────────────────────────────────────────
  // PLAY EVENT TRACKING
  // ─────────────────────────────────────────────────────────────

  public shouldEmitPlay(instanceId: string): boolean {
    return this.lastPlayEmittedForId !== instanceId;
  }

  public markPlayEmitted(instanceId: string): void {
    this.lastPlayEmittedForId = instanceId;
  }

  public resetPlayTracking(): void {
    this.lastPlayEmittedForId = undefined;
  }
}
